import {
  prisma,
  CheckInOutType,
  PaymentMethod,
  PaymentStatus,
  PricingRuleType,
  type Currency,
} from "client";
import { minorToMajorForBreakdownTruncated } from "@sajilni/money/helpers";

export type OrderDetailSection = "payment" | "fulfillment";

export type OrderPaymentDetails = {
  applicable: boolean;
  paymentMethod: string | null;
  transactionId: string | null;
  promoCode: string | null;
  discountAmount: number | null;
  fees: {
    platform: number;
    payment: number;
    service: number;
    total: number;
  };
  netRevenue: number | null;
  gatewayMessage: string | null;
  currency: string | null;
};

export type OrderFulfillmentItem = {
  orderItemId: string;
  itemName: string | null;
  checkInStatus: "CHECKED_IN" | "CHECKED_OUT" | "NOT_CHECKED_IN";
  checkInTime: string | null;
};

export type OrderFulfillmentDetails = {
  items: OrderFulfillmentItem[];
  refundStatus: "Yes" | "No";
  summary: {
    checkedIn: number;
    notCheckedIn: number;
    checkedOut: number;
    total: number;
  };
};

export type OrderDetailSectionsResult = {
  orderId: string;
  payment?: OrderPaymentDetails;
  fulfillment?: OrderFulfillmentDetails;
};

const toMajor = (amountMinor: number | null | undefined, currency: string) => {
  if (amountMinor == null) return 0;
  return minorToMajorForBreakdownTruncated({
    amountMinor,
    currency,
  });
};

const extractGatewayMessage = (
  gatewayResponse: unknown,
  transitionMeta: unknown
): string | null => {
  const meta = transitionMeta as { gatewayMessage?: string } | null;
  if (meta?.gatewayMessage && typeof meta.gatewayMessage === "string") {
    return meta.gatewayMessage;
  }

  const response = gatewayResponse as {
    result?: { description?: string };
    message?: string;
    description?: string;
  } | null;

  if (response?.result?.description) return response.result.description;
  if (response?.message) return response.message;
  if (response?.description) return response.description;
  return null;
};

const computeCheckInStatus = (
  checks: Array<{ type: CheckInOutType; time: Date }>
): {
  status: OrderFulfillmentItem["checkInStatus"];
  checkInTime: string | null;
} => {
  if (!checks.length) {
    return { status: "NOT_CHECKED_IN", checkInTime: null };
  }

  const sorted = [...checks].sort(
    (a, b) => b.time.getTime() - a.time.getTime()
  );
  const latest = sorted[0];

  if (latest.type === CheckInOutType.CHECK_OUT) {
    const lastCheckIn = sorted.find((c) => c.type === CheckInOutType.CHECK_IN);
    return {
      status: "CHECKED_OUT",
      checkInTime: lastCheckIn?.time?.toISOString() ?? null,
    };
  }

  return {
    status: "CHECKED_IN",
    checkInTime: latest.time.toISOString(),
  };
};

const resolveItemName = (item: {
  ticket?: {
    bookableItem?: { translations?: Array<{ name?: string | null }> } | null;
  } | null;
  entity?: {
    label?: string | null;
    entityTier?: {
      bookableItem?: { translations?: Array<{ name?: string | null }> } | null;
    } | null;
  } | null;
}): string | null => {
  const ticketName = item.ticket?.bookableItem?.translations?.[0]?.name;
  if (ticketName) return ticketName;

  const entityTierName =
    item.entity?.entityTier?.bookableItem?.translations?.[0]?.name;
  if (entityTierName) return entityTierName;

  return item.entity?.label ?? null;
};

const pickPrimaryPayment = <
  T extends {
    status: PaymentStatus;
    paymentMethod?: { type: PaymentMethod } | null;
    createdAt: Date;
  }
>(
  payments: T[]
): T | null => {
  if (!payments.length) return null;

  const paid = payments.find((p) => p.status === PaymentStatus.PAID);
  if (paid) return paid;

  const refunded = payments.find((p) => p.status === PaymentStatus.REFUNDED);
  if (refunded) return refunded;

  return [...payments].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
  )[0];
};

const isPaymentApplicable = (
  payment: {
    status: PaymentStatus;
    grandTotal: number | null;
    paymentMethod?: { type: PaymentMethod } | null;
  } | null,
  orderTotal: number | null
): boolean => {
  if (!payment) return (orderTotal ?? 0) > 0;
  if (payment.paymentMethod?.type === PaymentMethod.FREE) return false;
  if ((payment.grandTotal ?? 0) <= 0 && (orderTotal ?? 0) <= 0) return false;
  return true;
};

export const getOrderPaymentDetails = async (
  orderId: string
): Promise<OrderPaymentDetails> => {
  const order = await prisma.order.findUnique({
    where: { id: orderId },
    select: {
      id: true,
      total: true,
      currency: true,
      payments: {
        orderBy: { createdAt: "desc" },
        include: {
          paymentMethod: {
            select: {
              type: true,
            },
          },
          transitions: {
            where: { mostRecent: true },
            take: 1,
            select: { metaData: true },
          },
          appliedPricingAdjustments: {
            select: {
              amount: true,
              breakdown: true,
              pricingRule: {
                select: {
                  type: true,
                  previewLabel: true,
                  name: true,
                },
              },
            },
          },
        },
      },
      promoCodeUsage: {
        include: {
          pricingRule: {
            select: {
              name: true,
              previewLabel: true,
              discountConfig: true,
            },
          },
        },
      },
      appliedPricingAdjustments: {
        select: {
          amount: true,
          breakdown: true,
          pricingRule: {
            select: {
              type: true,
              previewLabel: true,
              name: true,
            },
          },
        },
      },
    },
  });

  if (!order) {
    throw new Error("Order not found");
  }

  const currency = (order.currency ?? "SAR") as Currency;
  const payment = pickPrimaryPayment(order.payments);
  const applicable = isPaymentApplicable(payment, order.total);

  if (!applicable || !payment) {
    return {
      applicable: false,
      paymentMethod: payment?.paymentMethod?.type ?? PaymentMethod.FREE,
      transactionId: null,
      promoCode: null,
      discountAmount: null,
      fees: { platform: 0, payment: 0, service: 0, total: 0 },
      netRevenue: null,
      gatewayMessage: null,
      currency,
    };
  }

  const promoFromUsage = order.promoCodeUsage
    .map((usage) => {
      const code = usage.pricingRule?.discountConfig?.code;
      return code || usage.pricingRule?.name || usage.pricingRule?.previewLabel;
    })
    .filter(Boolean)
    .join(", ");

  const allAdjustments = [
    ...order.appliedPricingAdjustments,
    ...(payment.appliedPricingAdjustments ?? []),
  ];

  let platformFeesMinor = 0;
  let paymentFeesMinor = payment.paymentFees ?? 0;
  let serviceFeesMinor = 0;
  let discountMinor = payment.totalDiscountAmount ?? 0;

  for (const adj of allAdjustments) {
    const ruleType = adj.pricingRule?.type;
    const label = (
      adj.pricingRule?.previewLabel ||
      adj.pricingRule?.name ||
      ""
    ).toLowerCase();
    const amount = Math.abs(adj.amount ?? 0);

    if (ruleType === PricingRuleType.DISCOUNT) {
      if (!payment.totalDiscountAmount) {
        discountMinor += amount;
      }
      continue;
    }

    if (ruleType !== PricingRuleType.CHARGE) continue;

    if (label.includes("platform")) {
      platformFeesMinor += amount;
    } else if (label.includes("payment") || label.includes("gateway")) {
      // Prefer Payment.paymentFees when set; otherwise accumulate from adjustments
      if (payment.paymentFees == null) {
        paymentFeesMinor += amount;
      }
    } else {
      serviceFeesMinor += amount;
    }
  }

  const platform = toMajor(platformFeesMinor, currency);
  const paymentFee = toMajor(paymentFeesMinor, currency);
  const service = toMajor(serviceFeesMinor, currency);
  const feesTotal = platform + paymentFee + service;
  const paidMajor = toMajor(payment.grandTotal ?? order.total ?? 0, currency);
  const netRevenue = Number((paidMajor - feesTotal).toFixed(2));

  return {
    applicable: true,
    paymentMethod: payment.paymentMethod?.type ?? null,
    transactionId: payment.gatewayReferenceId ?? null,
    promoCode: promoFromUsage || null,
    discountAmount: discountMinor > 0 ? toMajor(discountMinor, currency) : null,
    fees: {
      platform,
      payment: paymentFee,
      service,
      total: feesTotal,
    },
    netRevenue,
    gatewayMessage: extractGatewayMessage(
      payment.gatewayResponse,
      payment.transitions?.[0]?.metaData
    ),
    currency,
  };
};

export const getOrderFulfillmentDetails = async (
  orderId: string
): Promise<OrderFulfillmentDetails> => {
  const order = await prisma.order.findUnique({
    where: { id: orderId },
    select: {
      id: true,
      payments: {
        select: { status: true },
        orderBy: { createdAt: "desc" },
      },
      orderItems: {
        select: {
          id: true,
          ticket: {
            select: {
              bookableItem: {
                select: {
                  translations: {
                    take: 1,
                    select: { name: true },
                  },
                },
              },
            },
          },
          entity: {
            select: {
              label: true,
              entityTier: {
                select: {
                  bookableItem: {
                    select: {
                      translations: {
                        take: 1,
                        select: { name: true },
                      },
                    },
                  },
                },
              },
            },
          },

          checksInOut: {
            where: {
              OR: [{ deletedAt: null }, { deletedAt: { isSet: false } }],
            },
            select: { type: true, time: true },
            orderBy: { time: "desc" },
          },
        },
      },
    },
  });

  if (!order) {
    throw new Error("Order not found");
  }

  const items: OrderFulfillmentItem[] = order.orderItems.map((item) => {
    const { status, checkInTime } = computeCheckInStatus(item.checksInOut);
    return {
      orderItemId: item.id,
      itemName: resolveItemName(item),
      checkInStatus: status,
      checkInTime,
    };
  });

  const summary = {
    checkedIn: items.filter((i) => i.checkInStatus === "CHECKED_IN").length,
    notCheckedIn: items.filter((i) => i.checkInStatus === "NOT_CHECKED_IN")
      .length,
    checkedOut: items.filter((i) => i.checkInStatus === "CHECKED_OUT").length,
    total: items.length,
  };

  const refunded = order.payments.some(
    (p) => p.status === PaymentStatus.REFUNDED
  );

  return {
    items,
    refundStatus: refunded ? "Yes" : "No",
    summary,
  };
};

export const getOrderDetailSections = async (
  orderId: string,
  sections: OrderDetailSection[]
): Promise<OrderDetailSectionsResult> => {
  const unique = Array.from(new Set(sections));
  const result: OrderDetailSectionsResult = { orderId };

  const tasks: Promise<void>[] = [];
  if (unique.includes("payment")) {
    tasks.push(
      getOrderPaymentDetails(orderId).then((payment) => {
        result.payment = payment;
      })
    );
  }

  if (unique.includes("fulfillment")) {
    tasks.push(
      getOrderFulfillmentDetails(orderId).then((fulfillment) => {
        result.fulfillment = fulfillment;
      })
    );
  }

  await Promise.all(tasks);
  return result;
};
